import {
  ClauseDetailRequestSchema,
  ClauseListRequestSchema,
  ENDPOINTS,
  ManageClauseRequestSchema,
  type ClauseDetailResponse,
  type ClauseListRequest,
  type ClauseListResponse,
  type ManageClauseRequest,
  type ManageClauseResponse,
} from '@clm/shared-types';

import { apiGet, apiPost, type Query } from './client';
import { substitutePath } from './verifications';

/**
 * `clauses`, `clause-detail` and `manage-clause` — the clause library, wrapped
 * for the browser (CLM-55). `verifications.ts` is the precedent: the path comes
 * from the registry, the request is `.parse`d before it leaves the browser, and
 * the response is **parsed** rather than asserted, so an off-contract answer is
 * a `ZodError` here and never data on screen.
 *
 * The list is a GET, so the request object is flattened the one way
 * `apps/api/src/http/decode.ts` reads: dotted keys for the nested page object
 * (`page.limit`, `page.cursor`) and booleans as the literal `true`/`false`.
 */

const LIST_ENDPOINT = ENDPOINTS.clauses;
const DETAIL_ENDPOINT = ENDPOINTS['clause-detail'];
const MANAGE_ENDPOINT = ENDPOINTS['manage-clause'];

/** Flatten a validated list request into the query the decoder expands. */
export function clauseListQuery(request: ClauseListRequest): Query {
  const query: Record<string, string | number | boolean> = {};
  if (request.include_inactive !== undefined) query['include_inactive'] = request.include_inactive;
  if (request.category !== undefined) query['category'] = request.category;
  if (request.page?.limit !== undefined) query['page.limit'] = request.page.limit;
  if (request.page?.cursor !== undefined) query['page.cursor'] = request.page.cursor;
  return query;
}

export async function fetchClauses(request: ClauseListRequest, signal?: AbortSignal): Promise<ClauseListResponse> {
  // `.strict()`, so an invented or scope-shaped key is a ZodError here rather
  // than a parameter on the wire.
  const validated = ClauseListRequestSchema.parse(request);
  const raw = await apiGet<unknown>(LIST_ENDPOINT.path, {
    query: clauseListQuery(validated),
    ...(signal === undefined ? {} : { signal }),
  });
  return LIST_ENDPOINT.response.parse(raw);
}

export async function fetchClauseDetail(clause_code: string, signal?: AbortSignal): Promise<ClauseDetailResponse> {
  const validated = ClauseDetailRequestSchema.parse({ clause_code });
  const path = substitutePath(DETAIL_ENDPOINT.path, { clause_code: validated.clause_code });
  const raw = await apiGet<unknown>(path, signal === undefined ? {} : { signal });
  return DETAIL_ENDPOINT.response.parse(raw);
}

/**
 * `POST /v1/clauses/actions`. The body **is** the action — no envelope — and
 * every member of the union is `.strict()`, so an extra key is refused before
 * `apiPost` is called.
 */
export async function manageClause(request: ManageClauseRequest, signal?: AbortSignal): Promise<ManageClauseResponse> {
  const body = ManageClauseRequestSchema.parse(request);
  const raw = await apiPost<unknown>(MANAGE_ENDPOINT.path, body, signal === undefined ? {} : { signal });
  return MANAGE_ENDPOINT.response.parse(raw);
}
